/**
 * ReproductiveAxisCard — iter1060 — три въпроса, на които календарът не
 * отговаря: има ли овулация, валидни ли са фазите при Вашия препарат и на
 * кой етаж е спирачката при нередовен цикъл.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { ArrowRight, ChevronDown, CircleDot, Loader2, Pill, Target } from "lucide-react";
import { toast } from "sonner";

const OV_TONE = {
  confirmed: { light: "bg-emerald-50 border-emerald-300", dark: "bg-emerald-900/25 border-emerald-500/40" },
  probable: { light: "bg-sky-50 border-sky-200", dark: "bg-sky-900/20 border-sky-500/30" },
  short_luteal: { light: "bg-amber-50 border-amber-300", dark: "bg-amber-900/25 border-amber-500/40" },
  anovulatory_suspect: { light: "bg-rose-50 border-rose-200", dark: "bg-rose-900/20 border-rose-500/30" },
  no_data: { light: "bg-slate-50 border-slate-200", dark: "bg-slate-800/40 border-slate-700" },
};

const FLOOR_TONE = {
  hypothalamic: "border-amber-400",
  pituitary_prolactin: "border-violet-400",
  ovarian: "border-rose-400",
  pcos_pattern: "border-orange-400",
  needs_labs: "border-slate-400",
  suppressed: "border-sky-400",
  unclear: "border-slate-400",
  ok: "border-emerald-400",
};

export const ReproductiveAxisCard = ({ isDark }) => {
  const { API } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [signs, setSigns] = useState([]);
  const [bbt, setBbt] = useState("");
  const [busy, setBusy] = useState(false);
  const [why, setWhy] = useState(null);
  const [method, setMethod] = useState("none");
  const [started, setStarted] = useState("");

  useEffect(() => {
    axios.get(`${API}/reproductive`)
      .then(({ data }) => {
        setData(data);
        setSigns(data.ovulation?.selected || []);
        setMethod(data.contraception?.method || "none");
        setStarted(data.contraception?.started_on || "");
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [API]);

  const saveOv = async () => {
    setBusy(true);
    try {
      const { data: d } = await axios.post(`${API}/reproductive/ovulation`, {
        signs, bbt: bbt === "" ? null : parseFloat(bbt),
      });
      setData((p) => ({ ...p, ovulation: d }));
      toast.success(d.label_bg);
    } catch { toast.error("Записът не беше запазен."); }
    finally { setBusy(false); }
  };

  const saveContra = async (m, s) => {
    setBusy(true);
    try {
      const { data: d } = await axios.post(`${API}/reproductive/contraception`, {
        method: m, started_on: s || null,
      });
      const { data: full } = await axios.get(`${API}/reproductive`);
      setData(full);
      toast.success(d.method_bg);
    } catch { toast.error("Записът не беше запазен."); }
    finally { setBusy(false); }
  };

  const cardCls = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200";
  const box = isDark ? "bg-slate-800/50" : "bg-slate-100";
  const inputCls = `px-2 py-1.5 rounded-md border text-sm w-full ${isDark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-300"}`;

  if (loading) {
    return <div className={`p-5 rounded-2xl border ${cardCls} flex justify-center`} data-testid="reproductive-loading">
      <Loader2 className="w-5 h-5 animate-spin text-fuchsia-500" /></div>;
  }
  if (!data) return null;
  const ov = data.ovulation, contra = data.contraception, floor = data.cycle_floor;
  const tone = OV_TONE[ov.verdict] || OV_TONE.no_data;

  return (
    <div className={`p-5 rounded-2xl border-2 ${cardCls}`} data-testid="reproductive-axis-card">
      <button onClick={() => setOpen(!open)} className="w-full text-left flex items-center gap-2"
              data-testid="reproductive-toggle">
        <Target className="w-4 h-4 text-fuchsia-500 shrink-0" />
        <h3 className="font-bold text-sm flex-1 min-w-0">Репродуктивна ос</h3>
        <span className="text-[10px] px-2 py-1 rounded-md font-bold bg-fuchsia-600 text-white shrink-0 max-w-[45%] truncate"
              data-testid="reproductive-badge">{ov.label_bg}</span>
        <ChevronDown className={`w-4 h-4 opacity-50 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <p className="text-[11px] opacity-75 leading-relaxed mt-1">{data.intro_bg}</p>

      {!contra.phases_apply && (
        <div className={`mt-2 p-3 rounded-xl border ${isDark ? "bg-sky-900/20 border-sky-500/40" : "bg-sky-50 border-sky-200"}`}
             data-testid="reproductive-phases-off">
          <div className="text-[11px] font-bold">Фазите на цикъла не важат при {contra.method_bg}</div>
          <div className="text-[10px] opacity-85 mt-0.5 leading-relaxed">{contra.notes_bg[0]}</div>
        </div>
      )}

      {open && (
        <div className="mt-4 space-y-5">
          {/* 1 — овулация */}
          <div>
            <div className="text-[11px] font-bold mb-1.5 flex items-center gap-1">
              <CircleDot className="w-3.5 h-3.5 text-fuchsia-500" />
              1. Потвърждение на овулацията
            </div>
            <div className="space-y-1" data-testid="reproductive-signs">
              {ov.signs.map((s) => (
                <div key={s.key} className={`rounded-xl ${box}`}>
                  <div className="p-2 flex items-start gap-2">
                    <button onClick={() => setSigns(signs.includes(s.key)
                              ? signs.filter((k) => k !== s.key) : [...signs, s.key])}
                            data-testid={`repro-sign-${s.key}`} role="checkbox"
                            aria-checked={signs.includes(s.key)}
                            className={`shrink-0 w-4 h-4 mt-0.5 rounded border ${signs.includes(s.key)
                              ? "bg-fuchsia-600 border-fuchsia-600" : isDark ? "border-slate-600" : "border-slate-400"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] font-bold leading-snug">{s.label_bg}</div>
                      <button onClick={() => setWhy(why === s.key ? null : s.key)}
                              className="text-[10px] opacity-50 hover:opacity-100"
                              data-testid={`repro-why-${s.key}`}>защо?</button>
                      {why === s.key && (
                        <div className="text-[10px] opacity-80 mt-0.5 leading-relaxed">{s.why_bg}</div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <label className="flex flex-col gap-1">
                <span className="text-[10px] opacity-70">Базална температура °C</span>
                <input type="number" step="0.01" className={inputCls} value={bbt}
                       data-testid="repro-bbt" onChange={(e) => setBbt(e.target.value)} />
              </label>
              <button onClick={saveOv} disabled={busy} data-testid="repro-save-ovulation"
                      className="self-end py-2 rounded-lg bg-fuchsia-600 text-white text-[11px] font-bold disabled:opacity-60">
                {busy ? "Записвам…" : "Запишете за днес"}
              </button>
            </div>

            <div className={`mt-2 p-3 rounded-xl border ${isDark ? tone.dark : tone.light}`}
                 data-testid="reproductive-ovulation-verdict">
              <div className="flex items-baseline gap-2">
                <span className="text-xs font-bold" data-testid="repro-ov-label">{ov.label_bg}</span>
                <span className="ml-auto text-[10px] opacity-60 tabular-nums">{ov.score} т.</span>
              </div>
              <div className="text-[11px] opacity-85 mt-1 leading-relaxed">{ov.text_bg}</div>
              {ov.luteal_length != null && (
                <div className="text-[10px] opacity-75 mt-1.5" data-testid="repro-luteal-length">
                  Лутеална фаза: {ov.luteal_length} дни (нормата е 12-16).
                </div>
              )}
              <div className="text-[10px] opacity-75 mt-1.5" data-testid="repro-calendar-note">
                {ov.calendar_note_bg}
              </div>
              <ul className="text-[10px] opacity-75 mt-1.5 space-y-0.5">
                {ov.how_bg.map((h, i) => <li key={i}>• {h}</li>)}
              </ul>
            </div>
          </div>

          {/* 2 — контрацепция */}
          <div>
            <div className="text-[11px] font-bold mb-1.5 flex items-center gap-1">
              <Pill className="w-3.5 h-3.5 text-fuchsia-500" />
              2. Контрацепцията като контекст
            </div>
            <p className="text-[10px] opacity-75 leading-relaxed mb-2">{contra.why_bg}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <label className="flex flex-col gap-1">
                <span className="text-[10px] opacity-70">Метод</span>
                <select className={inputCls} value={method} data-testid="repro-method"
                        onChange={(e) => { setMethod(e.target.value); saveContra(e.target.value, started); }}>
                  {contra.methods.map((m) => (
                    <option key={m.key} value={m.key}>{m.label_bg}</option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] opacity-70">От дата</span>
                <input type="date" className={inputCls} value={started || ""} data-testid="repro-started"
                       onChange={(e) => { setStarted(e.target.value); saveContra(method, e.target.value); }} />
              </label>
            </div>

            {contra.notes_bg?.length > 0 && (
              <ul className="text-[10px] opacity-85 mt-2 space-y-1" data-testid="reproductive-contra-notes">
                {contra.notes_bg.map((n, i) => <li key={i}>• {n}</li>)}
              </ul>
            )}
            {contra.lab_cautions?.length > 0 && (
              <div className="mt-2 space-y-1.5" data-testid="reproductive-lab-cautions">
                <div className="text-[11px] font-bold">Кои лаборатории лъжат при този препарат</div>
                {contra.lab_cautions.map((c, i) => (
                  <div key={i} className={`p-2.5 rounded-xl ${box}`} data-testid={`repro-caution-${i}`}>
                    <div className="text-[11px] font-bold">{c.marker_bg}</div>
                    <div className="text-[10px] opacity-80 mt-0.5 leading-relaxed">{c.text_bg}</div>
                  </div>
                ))}
                <button onClick={() => navigate(contra.route_labs)} data-testid="repro-labs-link"
                        className={`text-[10px] px-2 py-1 rounded-lg border flex items-center gap-1 ${isDark ? "border-slate-700" : "border-slate-300"}`}>
                  Лабораторен близнак <ArrowRight className="w-3 h-3" />
                </button>
              </div>
            )}
            {contra.micronutrients_bg?.length > 0 && (
              <div className="text-[10px] opacity-75 mt-2" data-testid="repro-micronutrients">
                Изчерпват се по-бързо: {contra.micronutrients_bg.join(", ")}.
              </div>
            )}
          </div>

          {/* 3 — етажът */}
          <div>
            <div className="text-[11px] font-bold mb-1.5">3. Къде е спирачката при нередовен цикъл</div>
            <div className={`p-3 rounded-xl border-l-4 ${FLOOR_TONE[floor.status] || "border-slate-400"} ${box}`}
                 data-testid="reproductive-floor">
              <div className="text-xs font-bold" data-testid="repro-floor-label">{floor.label_bg}</div>
              <div className="text-[11px] opacity-85 mt-1 leading-relaxed">{floor.text_bg}</div>
              {floor.values?.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 mt-2" data-testid="repro-floor-values">
                  {floor.values.map((v, i) => (
                    <div key={i} className={`p-1.5 rounded-lg text-center ${isDark ? "bg-slate-900/60" : "bg-white"}`}>
                      <div className="text-[9px] opacity-60">{v.label_bg}</div>
                      <div className="text-[11px] font-bold tabular-nums">{v.value} <span className="opacity-50 font-normal">{v.unit}</span></div>
                    </div>
                  ))}
                </div>
              )}
              {floor.missing_bg?.length > 0 && (
                <div className="text-[10px] opacity-60 mt-1.5" data-testid="repro-floor-missing">
                  Липсва: {floor.missing_bg.join(", ")}.
                </div>
              )}
              {floor.energy && (
                <div className="text-[10px] opacity-75 mt-1.5" data-testid="repro-floor-energy">
                  Енергиен ресурс: {floor.energy.label_bg}
                  {floor.energy.drivers_bg?.length > 0 ? ` — ${floor.energy.drivers_bg.join("; ")}` : ""}.
                </div>
              )}
              <div className="text-[10px] opacity-70 mt-1.5 leading-relaxed">{floor.feedback_bg}</div>
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid="repro-floor-links">
                {floor.links.map((l, i) => (
                  <button key={i} onClick={() => navigate(l.route)} data-testid={`repro-floor-link-${i}`}
                          className={`text-[10px] px-2 py-1 rounded-lg border flex items-center gap-1 ${isDark ? "border-slate-700" : "border-slate-300"}`}>
                    {l.twin_bg} <ArrowRight className="w-3 h-3" /> <span className="opacity-70">{l.why_bg}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReproductiveAxisCard;
